import type { FinancialStatus, OrderWithDetails } from "@estelle/domain";
import type { ReactNode } from "react";

import { StatusPill } from "@/components/table/StatusPill";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { dateTime, money } from "@/modules/orders/format";

const STATUS_LABEL: Record<FinancialStatus, string> = {
  pending: "Payment pending",
  authorized: "Authorized",
  partially_paid: "Partially paid",
  paid: "Paid",
  partially_refunded: "Partially refunded",
  refunded: "Refunded",
  voided: "Voided",
};

const KIND_LABEL: Record<string, string> = {
  SALE: "Sale",
  CAPTURE: "Capture",
  AUTHORIZATION: "Authorization",
  VOID: "Void",
  REFUND: "Refund",
  CHANGE: "Change",
};

function rawOf(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function list(raw: unknown, key: string): Record<string, unknown>[] {
  const value = rawOf(raw)[key];
  return Array.isArray(value) ? value.map(rawOf) : [];
}

function edges(value: unknown): Record<string, unknown>[] {
  const inner = rawOf(value).edges;
  return Array.isArray(inner)
    ? inner.map((edge) => rawOf(rawOf(edge).node))
    : [];
}

function amountOf(set: unknown): string | null {
  const amount = rawOf(rawOf(set).shopMoney).amount;
  return typeof amount === "string" ? amount : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function Row({
  label,
  middle,
  value,
  strong,
  className,
}: {
  label: string;
  middle?: ReactNode;
  value: ReactNode;
  strong?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[7rem_minmax(0,1fr)_auto] gap-3 py-1 text-sm",
        strong && "font-medium",
        className,
      )}
    >
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span className="text-muted-foreground truncate">{middle}</span>
      <span className="text-right tabular-nums">{value}</span>
    </div>
  );
}

/**
 * The payment card, as Shopify's order page lays it out (#327): subtotal,
 * discount, the shipping method and its charge, each tax line with its rate —
 * "Included" on this tax-inclusive store — the total, what has been paid and
 * what is still owed; then refunds and every transaction (FR-ORD-3f, 3g).
 *
 * Shipping line, tax lines, refunds and transactions come from the channel
 * payload: they are shown on one order at a time and nothing filters on them.
 */
export function OrderPayments({ order }: { order: OrderWithDetails }) {
  const raw = order.raw;
  const itemCount = order.lineItems.reduce(
    (sum, line) => sum + line.quantity,
    0,
  );
  const shippingLine = edges(rawOf(raw).shippingLines)[0];
  const taxLines = list(raw, "taxLines");
  const transactions = list(raw, "transactions");
  const refunds = list(raw, "refunds");
  const paid =
    Number(order.total) -
    Number(order.totalOutstanding) -
    Number(order.totalRefunded);
  const outstanding = Number(order.totalOutstanding) > 0;

  return (
    <Card className="gap-3">
      <CardHeader>
        <div>
          {order.financialStatus ? (
            <StatusPill
              value={order.financialStatus}
              label={STATUS_LABEL[order.financialStatus]}
            />
          ) : (
            <Badge variant="outline">Payment status unknown</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border px-3 py-2">
          <Row
            label="Subtotal"
            middle={`${String(itemCount)} item${itemCount === 1 ? "" : "s"}`}
            value={money(order.subtotal)}
          />
          {Number(order.totalDiscount) > 0 ? (
            <Row
              label="Discount"
              middle={order.discountCodes.join(", ") || null}
              value={<>−{money(order.totalDiscount)}</>}
            />
          ) : null}
          <Row
            label="Shipping"
            middle={text(shippingLine?.title)}
            value={money(order.totalShipping)}
          />
          {taxLines.length > 0 ? (
            taxLines.map((line, index) => (
              <Row
                key={index}
                label={index === 0 ? "Taxes" : ""}
                middle={
                  <>
                    {money(amountOf(line.priceSet))} ·{" "}
                    {text(line.title) ?? "Tax"}{" "}
                    {typeof line.ratePercentage === "number"
                      ? `${String(line.ratePercentage)}%`
                      : ""}
                  </>
                }
                value={
                  order.taxesIncluded
                    ? "Included"
                    : money(amountOf(line.priceSet))
                }
              />
            ))
          ) : (
            <Row label="Taxes" middle={null} value={money(order.totalTax)} />
          )}
          <Row
            label="Total"
            value={money(order.total)}
            strong
            className="border-t pt-2"
          />
          <Row
            label="Paid"
            value={money(String(Math.max(0, paid)))}
            className="border-t pt-2"
          />
          {Number(order.totalRefunded) > 0 ? (
            <Row
              label="Refunded"
              value={
                <span className="text-destructive">
                  {money(order.totalRefunded)}
                </span>
              }
            />
          ) : null}
          <Row
            label="Balance"
            value={
              <span className={outstanding ? "text-primary-strong" : ""}>
                {money(order.totalOutstanding)}
              </span>
            }
            strong={outstanding}
          />
        </div>

        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <p className="text-muted-foreground mb-1 text-xs font-medium">
              Transactions
            </p>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {transactions.map((t, index) => (
                  <tr
                    key={text(t.id) ?? index}
                    className="border-b last:border-b-0"
                  >
                    <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">
                      {dateTime(text(t.processedAt) ?? text(t.createdAt))}
                    </td>
                    <td className="py-1.5 pr-3">
                      {KIND_LABEL[text(t.kind) ?? ""] ?? text(t.kind)}
                    </td>
                    <td className="py-1.5 pr-3">
                      {text(t.formattedGateway) ?? text(t.gateway) ?? "—"}
                    </td>
                    <td className="py-1.5 pr-3">
                      <StatusPill
                        value={(text(t.status) ?? "").toLowerCase()}
                      />
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {money(amountOf(t.amountSet))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {refunds.length > 0 ? (
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium">Refunds</p>
            {refunds.map((refund, index) => (
              <div
                key={text(refund.id) ?? index}
                className="flex items-baseline justify-between gap-3 text-sm"
              >
                <span className="tabular-nums">
                  {dateTime(text(refund.createdAt))}
                  {text(refund.note) ? (
                    <span className="text-muted-foreground">
                      {" "}
                      · {text(refund.note)}
                    </span>
                  ) : null}
                </span>
                <span className="text-destructive tabular-nums">
                  {money(amountOf(refund.totalRefundedSet))}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
